import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFreshBuildUrl } from '../../hooks/useFreshBuildUrl';
import type { VersionJobActivity } from '../../hooks/useVersionJob';
import { EditorHost, RequestFailure, TunableValues } from '../../services/editorHost';
import {
  askChat,
  exposeTunable,
  getVersionLayout,
  getVersionTunables,
  organizeTunables,
  savePendingTunables,
} from '../../services/tunables';
import { actionFailureMessage, rateLimitMessage } from '../../utils/actionFailure';

/** The editor page's view in the right-hand area: the tuning panel, or the scenes of the game. */
export type EditorView = 'panel' | 'scenes';

export const EditorViewTitle: Record<EditorView, string> = {
  panel: 'editor.title',
  scenes: 'editor.tabScenes',
};

interface EditorFrameProps {
  projectId: string;
  versionId: string;
  /** The page's build address, loaded only when a fresh one cannot be minted as the frame mounts. */
  buildUrl: string;
  /** Read by the editor page once, when it loads; a caller that changes it keys the frame by it, so it remounts. */
  view: EditorView;
  interactive: boolean;
  /** A job that produces a version is queued or running; the editor keeps changes with the version it shows. */
  jobRunning: boolean;
  /** The version whose values were just arranged into panels, or null. */
  arrangedVersionId: string | null;
  /** Shows the creator a short message, already translated. */
  showNotice: (text: string) => void;
  onVersionJobStarted: (jobId: string, activity: VersionJobActivity) => void;
  onOrganizeStarted: () => void;
}

export default function EditorFrame({
  projectId,
  versionId,
  buildUrl,
  view,
  interactive,
  jobRunning,
  arrangedVersionId,
  showNotice,
  onVersionJobStarted,
  onOrganizeStarted,
}: EditorFrameProps) {
  const { t, i18n } = useTranslation();
  const frame = useRef<HTMLIFrameElement>(null);
  const host = useRef<EditorHost | null>(null);
  const frameBuildUrl = useFreshBuildUrl(projectId, versionId, buildUrl);
  const origin = useMemo(() => new URL(frameBuildUrl ?? buildUrl).origin, [frameBuildUrl, buildUrl]);
  const language = i18n.language;
  const latestJobRunning = useRef(jobRunning);

  const session = useCallback(async () => {
    try {
      const [{ applied, pending }, layout] = await Promise.all([
        getVersionTunables(projectId, versionId),
        getVersionLayout(projectId, versionId),
      ]);
      return {
        applied,
        pending,
        layout,
        isOwner: true,
        language,
        jobRunning: latestJobRunning.current,
        organize: true,
      };
    } catch {
      showNotice(t('editor.loadFailed'));
      return null;
    }
  }, [projectId, versionId, language, showNotice, t]);

  const save = useCallback(
    async (pending: TunableValues) => {
      try {
        await savePendingTunables(projectId, versionId, pending);
      } catch (error) {
        showNotice(actionFailureMessage(error, t('editor.saveFailed')));
      }
    },
    [projectId, versionId, showNotice, t],
  );

  const expose = useCallback(
    async (text: string) => {
      const outcome = await exposeTunable(projectId, text);
      if (outcome.status === 'started') onVersionJobStarted(outcome.jobId, 'exposing');
      else if (outcome.status === 'busy') showNotice(t('editor.exposeBusy'));
      else showNotice(actionFailureMessage(outcome.error, t('editor.exposeFailed')));
    },
    [projectId, showNotice, onVersionJobStarted, t],
  );

  const organize = useCallback(async () => {
    const outcome = await organizeTunables(projectId, versionId);
    if (outcome.status === 'started') onOrganizeStarted();
    else if (outcome.status === 'busy') showNotice(t('editor.organizeBusy'));
    else showNotice(actionFailureMessage(outcome.error, t('editor.organizeFailed')));
  }, [projectId, versionId, showNotice, onOrganizeStarted, t]);

  const request = useCallback(
    async (text: string): Promise<RequestFailure | null> => {
      const outcome = await askChat(projectId, text);
      if (outcome.status === 'started') {
        onVersionJobStarted(outcome.jobId, 'versioning');
        return null;
      }
      // The editor words a failure itself; only the page knows how long a rate-limited creator waits.
      const wait = outcome.status === 'failed' ? rateLimitMessage(outcome.error) : null;
      if (wait) showNotice(wait);
      return outcome.status;
    },
    [projectId, showNotice, onVersionJobStarted],
  );

  useEffect(() => {
    const editor = new EditorHost(() => frame.current?.contentWindow ?? null, origin, {
      session,
      save,
      expose,
      organize,
      request,
    });
    host.current = editor;
    const stop = editor.listen();
    return () => {
      stop();
      host.current = null;
    };
  }, [origin, session, save, expose, organize, request]);

  useEffect(() => {
    latestJobRunning.current = jobRunning;
    host.current?.sendJobState(jobRunning);
  }, [jobRunning]);

  useEffect(() => {
    if (arrangedVersionId?.toLowerCase() !== versionId.toLowerCase()) return;
    getVersionLayout(projectId, versionId).then((layout) => {
      if (layout) host.current?.sendLayout(layout);
    });
  }, [arrangedVersionId, projectId, versionId]);

  if (!frameBuildUrl) return <div style={{ flex: 1 }} />;

  return (
    <iframe
      ref={frame}
      src={`${frameBuildUrl}/editor/index.html${view === 'scenes' ? '#scenes' : ''}`}
      title={t(EditorViewTitle[view])}
      style={{ flex: 1, border: 'none', width: '100%', pointerEvents: interactive ? 'auto' : 'none' }}
    />
  );
}
